import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ComponentsModule } from '../../../../components/components.module';
import { ConfirmationService } from '../../../../core/services/confirmation.service';

/**
 * Which flow shows the codes. The notice above the grid depends on it:
 * - first-setup: warning, save them, each code logs in once
 * - view: info, each code logs in once (the codes can be viewed again)
 * - regenerated: warning, the previous codes stopped working
 */
export type RecoveryCodesContext = 'first-setup' | 'view' | 'regenerated';

const RECOVERY_CODES_FILE_NAME = 'hotelcard-recovery-codes.txt';
/** How long the Copy button reads "Copied" after a copy */
export const COPIED_FEEDBACK_MS = 2000;

const NOTICE_KEY_BY_CONTEXT: Record<RecoveryCodesContext, string> = {
  'first-setup': 'profile.authenticators.recovery_codes_first_setup',
  view: 'profile.authenticators.recovery_codes_view',
  regenerated: 'profile.authenticators.recovery_codes_regenerated',
};

/**
 * Shows a set of recovery codes with the flow-specific notice and the copy
 * and download actions, lined up with the two code columns.
 */
@Component({
  selector: 'app-recovery-codes-list',
  templateUrl: './recovery-codes-list.component.html',
  styleUrl: './recovery-codes-list.component.scss',
  standalone: true,
  imports: [CommonModule, TranslateModule, ComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecoveryCodesListComponent implements OnDestroy {
  @Input() codes: string[] = [];
  @Input() context: RecoveryCodesContext = 'view';

  /** True for about two seconds after a successful copy */
  copied = false;
  private copiedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  get noticeKey(): string {
    return NOTICE_KEY_BY_CONTEXT[this.context];
  }

  /** Viewing existing codes is routine; the other two flows need attention. */
  get isWarning(): boolean {
    return this.context !== 'view';
  }

  ngOnDestroy(): void {
    this.clearCopiedTimer();
  }

  copyCodes(): void {
    if (this.codes.length === 0) {
      return;
    }
    navigator.clipboard.writeText(this.codes.join('\n')).then(
      () => this.showCopiedFeedback(),
      () => this.confirmationService.swalError(this.translate.instant('profile.authenticators.errors.copy_failed'))
    );
  }

  downloadCodes(): void {
    if (this.codes.length === 0) {
      return;
    }

    const fileText = 'HotelCard Extranet Recovery Codes\n' +
      '================================\n\n' +
      'Store these codes in a safe place.\n' +
      'Each code can only be used once.\n\n' +
      this.codes.join('\n');

    const blob = new Blob([fileText], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = RECOVERY_CODES_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    this.confirmationService.swalSuccess(this.translate.instant('profile.recovery_codes_downloaded'));
  }

  private showCopiedFeedback(): void {
    this.clearCopiedTimer();
    this.copied = true;
    this.cdr.markForCheck();
    this.copiedTimer = setTimeout(() => {
      this.copied = false;
      this.copiedTimer = null;
      this.cdr.markForCheck();
    }, COPIED_FEEDBACK_MS);
  }

  private clearCopiedTimer(): void {
    if (this.copiedTimer !== null) {
      clearTimeout(this.copiedTimer);
      this.copiedTimer = null;
    }
  }
}
